import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Currency for Qatar Riyal. Use this constant instead of hardcoding the code in
// labels, so every screen stays in sync with formatCurrency below.
export const CURRENCY = "QAR";

// Builds a field label with the currency code, e.g. amountLabel("Subtotal") -> "Subtotal (QAR)"
export function amountLabel(label: string): string {
  return `${label} (${CURRENCY})`;
}

// Formats a money amount with thousands separators and exactly two decimals.
// Accepts null/undefined/NaN because report payloads routinely omit a field, and
// a missing amount must render as "0.00" rather than crashing the whole tab —
// the previous `amount.toFixed(2)` threw on undefined and printed "NaN QAR".
export function formatAmount(amount: number | null | undefined): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) {
    return "0.00";
  }
  return value.toLocaleString("en-QA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Currency formatting for Qatar Riyal
export function formatCurrency(amount: number | null | undefined): string {
  return `${formatAmount(amount)} ${CURRENCY}`;
}

// Price adjustment sign conversion.
// The UI is signed the way a user reads it: + = surcharge (raises the price),
// - = discount (lowers it). The backend `Payment.discount` column is inverted:
// a positive value REDUCES the amount owed. Convert at every boundary.
export function toBackendDiscount(uiAdjustment: number): number {
  return -uiAdjustment;
}

export function toUiAdjustment(backendDiscount: number): number {
  return -backendDiscount;
}

// Helper function to validate and parse dates
function parseDate(date: Date | string | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? null : parsed;
}

// Date formatting
export function formatDate(date: Date | string | null | undefined): string {
  const parsed = parseDate(date);
  if (!parsed) return "Invalid date";
  return format(parsed, "dd/MM/yyyy");
}

// DateTime formatting
export function formatDateTime(date: Date | string | null | undefined): string {
  const parsed = parseDate(date);
  if (!parsed) return "Invalid date";
  return format(parsed, "dd/MM/yyyy HH:mm");
}

// Time formatting
export function formatTime(date: Date | string | null | undefined): string {
  const parsed = parseDate(date);
  if (!parsed) return "Invalid time";
  return format(parsed, "hh:mm a");
}

// Relative time (e.g., "2 days ago")
export function formatRelativeTime(
  date: Date | string | null | undefined,
): string {
  const parsed = parseDate(date);
  if (!parsed) return "Invalid date";

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - parsed.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800)
    return `${Math.floor(diffInSeconds / 86400)} days ago`;

  return formatDate(parsed);
}

// Phone formatting
export function formatPhone(phone: string): string {
  return phone.replace(/(\+974\s?)(\d{4})(\d{4})/, "+974 $2 $3");
}

// Generate invoice number
export function generateInvoiceNumber(count: number): string {
  const year = new Date().getFullYear();
  return `WC-${year}-${String(count + 1).padStart(4, "0")}`;
}

// Copy text to clipboard
export async function copyToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      textArea.remove();
    }
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    throw error;
  }
}

// Generate time slots with configurable interval
export function generateTimeSlots(
  startHour: number = 7,
  endHour: number = 23,
  intervalMinutes: number = 15,
): { value: string; label: string }[] {
  const slots: { value: string; label: string }[] = [];

  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      if (hour === endHour && minute > 0) break;

      const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const label = `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;

      slots.push({ value, label });
    }
  }
  return slots;
}
